"use client";

import CardReport from "@/app/[locale]/(user-page)/report/components/CardReport";
import FilterReport from "@/app/[locale]/(user-page)/report/components/FilterReport";
import { useAddFavoriteLatest } from "@/hooks/Query/useReport";
import { setIsShowFavorite } from "@/lib/features/reportSlice";
import { useAppDispatch } from "@/lib/hooks";
import { paramsReport } from "@/models/Params";
import { Report, TYPE_FAVORITE } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import DoneIcon from "@mui/icons-material/Done";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Button, MenuItem } from "@mui/material";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import React, { useEffect, useState } from "react";
import ThemeRegistry from "../../../../../../components/Theme/ThemeRegistry";

interface ReportsPublishedProps {
  queryKey?: string;
  categoryId?: string | number;
  onlyFavorite?: boolean;
  isLoggedIn?: boolean;
  userId?: number;
  isMobile?: boolean;
  typeFavorite?: number[];
  type: string;
  locale: string;
}

const montserrat = Montserrat({
  subsets: ["latin"],
});

const recommended = "Recommended";

const ReportsPublished = (props: ReportsPublishedProps) => {
  const {
    queryKey,
    categoryId,
    onlyFavorite,
    isLoggedIn,
    userId,
    isMobile,
    typeFavorite,
    type,
    locale,
  } = props;

  const t = useTranslations("messages");
  const dispatch = useAppDispatch();
  const dataSelectRecently = [
    {
      name: t("common_recommended"),
      isDescending: true,
      sortedField: "order",
    },
    {
      name: t("common_most_recent"),
      isDescending: true,
      sortedField: "publishedDate",
    },
    {
      name: t("common_oldest_first"),
      isDescending: false,
      sortedField: "publishedDate",
    },
  ];

  const dataSelectPurchased = [
    {
      name: t("common_newest_first"),
      isDescending: true,
      sortedField: "publishedDate",
    },
    {
      name: t("common_oldest_first"),
      isDescending: false,
      sortedField: "publishedDate",
    },
    {
      name: t("common_recently_purchased"),
      isDescending: true,
      sortedField: "publishedDate",
    },
  ];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const take = 9;
  const handleSelect = () => {
    if (type === "purchased") return dataSelectPurchased[0];
    return dataSelectRecently[0];
  };

  const [select, setSelect] = useState(handleSelect);
  const {
    data: recentlyData,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      queryKey,
      select.isDescending,
      select.sortedField,
      categoryId,
      type,
    ],
    queryFn: async ({ pageParam }: { pageParam?: number | string }) => {
      const params: paramsReport = {
        isHot: true,
        sortedField: select.sortedField,
        isDescending: select.name === recommended ? true : select.isDescending,
        take: take,
        isRecommended: select.name === recommended ? true : undefined,
        cursor: pageParam,
        onlyFavorite: type === "favorite" ? true : undefined,
        userId: type === "favorite" ? userId : undefined,
        categoryIds: categoryId,
        typeFavorite:
          type === "favorite" ? [TYPE_FAVORITE.FAVORITE] : undefined,
        lang: locale,
      };
      const result = await ReportServices.getAllReportHomePage(params);
      return result;
    },
    placeholderData: keepPreviousData,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor,
    select: (data) => {
      return {
        data: (data.pages as any).flatMap((item: any): Report[] => item.data),
        itemCount: (data.pages as any).flatMap(
          (item: any) => item.meta.itemCount
        ),
      };
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const handleShowFavorite = async () => {
      if (type === "favorite" && recentlyData?.data?.length === 0) {
        dispatch(setIsShowFavorite(false));
      }
    };
    handleShowFavorite();
  }, [recentlyData]);

  const AddFavorite = useAddFavoriteLatest({
    queryKey: [
      queryKey,
      select.isDescending,
      select.sortedField,
      categoryId,
      type,
    ],
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = async (value: any) => {
    setSelect(value);
    handleClose();
  };

  const handleAddFavorite = (id: number) => {
    AddFavorite.mutate(id);
  };

  //   router.push(`${APP_ROUTES.HOME.to}/${slug}`);
  // };

  // const handleRedirectDetailInteractive = (slug: string) => {
  //   router.push(`${APP_ROUTES.HOME.to}/${slug}/interactive`);
  // };
  // const handleRedirect = (slug: string, isHavePdf: boolean, isHaveInteractive: boolean) => {
  //   if (isHavePdf && isHaveInteractive) {
  //     router.push(`${APP_ROUTES.HOME.to}/${slug}`);
  //   } else if (isHavePdf) {
  //     router.push(`${APP_ROUTES.HOME.to}/${slug}`);
  //   } else if (isHaveInteractive) {
  //     router.push(`${APP_ROUTES.HOME.to}/${slug}/interactive`);
  //   } else {
  //     router.push(`${APP_ROUTES.HOME.to}/${slug}`);
  //   }
  // };
  const handleTitle = () => {
    switch (type) {
      case "favorite":
        return t("home_already_published");
      case "purchased":
        return t("home_your_purchased_reports");
      default:
        return t("home_recently_published");
    }
  };

  const handleStyleTitle = () => {
    switch (type) {
      case "favorite":
        return "!text-2xl/6 sm:!text-[32px]/[100%]";
      case "purchased":
        return "!text-2xl/6 sm:!text-[32px]/[48px]";
      default:
        return "!text-2xl/6 sm:!text-[32px]/[39.01px]";
    }
  };

  const handleDataSelect = () => {
    if (type === "purchased")
      return dataSelectPurchased.map((item, index) => (
        <MenuItem
          key={index}
          className={`${
            montserrat.className
          } px-4 py-2 gap-7 !text-sm/6 !font-normal normal-case ${
            item?.name === select.name
              ? "!bg-cimigo_blue_light_1 !text-white hover:!bg-ciBlue_dark_1 flex justify-between"
              : "text-gray_80"
          }`}
          onClick={() => handleChange(item)}
        >
          {item?.name}
          {item?.name === select.name && <DoneIcon className="w-5 h-5" />}
        </MenuItem>
      ));
    else
      return dataSelectRecently.map((item, index) => (
        <MenuItem
          key={index}
          className={`${
            montserrat.className
          } px-4 !py-2 gap-[16.42px] !text-sm/6 !font-normal normal-case ${
            item?.name === select.name
              ? "!bg-cimigo_blue_light_1 !text-white hover:!bg-ciBlue_dark_1 flex justify-between"
              : "text-gray_80"
          }`}
          onClick={() => handleChange(item)}
        >
          {item?.name}
          {item?.name === select.name && <DoneIcon className="w-5 h-5" />}
        </MenuItem>
      ));
  };

  return !(recentlyData as any)?.data?.length ? null : (
    <ThemeRegistry>
      {recentlyData?.data?.length > 0 ? (
        <div className="gap-4 grid">
          <Box className="py-4 sm:flex justify-between items-center">
            <Box>
              <p
                className={`!font-semibold ${handleStyleTitle()} !text-eerie_black m-0`}
              >
                {handleTitle()}
              </p>
            </Box>
            <Box className="flex justify-end mt-5 sm:mt-0">
              <Button
                variant="outlined"
                className={`!py-2 !px-4 gap-[2.47px] flex h-10 rounded border-1 !border-gray_20 report-filter-button`}
                onClick={handleClick}
              >
                <p
                  className={`text-base/6 font-normal normal-case text-gray_90 ${montserrat.className} m-0`}
                >
                  {select.name}
                </p>
                <KeyboardArrowUpIcon
                  className={`text-gray_60 ${anchorEl && "rotate-180"}`}
                />
              </Button>
              <FilterReport
                anchorEl={anchorEl}
                open={open}
                handleClose={handleClose}
                handleDataSelect={handleDataSelect}
              />
            </Box>
          </Box>
          <Box className="grid gap-[54px]">
            <Box className="grid grid-cols-12 gap-y-8 md:gap-[32px_24px]">
              {recentlyData?.data?.map((item: Report, index: number) => (
                <CardReport
                  key={`card-recently-${categoryId}-${item.id}-${index}`}
                  {...item}
                  isLogin={isLoggedIn || false}
                  onlyFavorite={onlyFavorite}
                  isMobile={isMobile}
                  handleAddFavorite={handleAddFavorite}
                />
              ))}
            </Box>

            {recentlyData?.data?.length >= take &&
            recentlyData?.data?.length !== recentlyData?.itemCount[0] ? (
              <Button
                variant="text"
                disabled={isFetchingNextPage}
                className="!py-2 gap-4 !bg-ciBlue_1 !rounded hover:cursor-pointer hover:!bg-ciBlue_2 show-more-reports-button"
                onClick={() => fetchNextPage()}
              >
                <Box className="py-2 px-4 gap-2 flex justify-center">
                  <p
                    className="font-medium normal-case text-base/6 text-ciBlue_900 m-0"
                    translation-key="common_show_more_reports"
                  >
                    {t("common_show_more_reports")}
                  </p>
                </Box>
              </Button>
            ) : null}
          </Box>
        </div>
      ) : null}
    </ThemeRegistry>
  );
};

export default ReportsPublished;
